import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { T } from "react-translator-component";
import "react-multi-carousel/lib/styles.css";
import ButtonOutlinedCentered from "./../ButtonOutlined";
import { colors } from "../../common/colors";
import Separator from "./../Separator";
import SelectedUser from "./../items/SelectedUser";
import { useRef, useState } from "react";
import AddUserModal from "./AddUserModal";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../redux/store";
import Carousel from "react-multi-carousel";
import "../../App.css";
import { PrivateUser, UserEntity } from "../../util/CreatePost/types";
import { setItem } from "../../redux/Reducers/createPostReducer";

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: flex-end; /* Align items to the bottom */
  z-index: 9999;
`;

const ModalContainer = styled.div`
  background: white;
  border-radius: 8px;
  width: 100%;
  height: 92%;
  overflow-y: auto;
  z-index: 9999;
`;

interface Props {
  isEdit: boolean;
  isOnlyPrivate: boolean;
  onAdd: (user: any) => void;
  onDelete: (user: UserEntity) => void;
}

export default function SelectPostType({ isEdit, isOnlyPrivate, onAdd, onDelete }: Props) {
  const dispatch = useAppDispatch();
  const post = useSelector((state: any) => state.createPost.values);
  const isPrivate = post.isPrivate;
  const privateUsers = post.privateUsers.filter((privateUser: PrivateUser) => privateUser._destroy !== 1);
  const [isUserModelVisible, setIsUserModelVisible] = useState(false);
  const carouselRef = useRef<Carousel>(null);

  const handleAddUserClick = () => {
    setIsUserModelVisible(true);
  };

  const handleCloseModal = () => {
    setIsUserModelVisible(false);
  };

  const handleThreadType = (value: string) => {
    dispatch(setItem({key: 'isPrivate', value: value === "true"}))
  };

  const handleDelete = (user: UserEntity) => {
    if (carouselRef.current) {
      carouselRef.current.goToSlide(0);
    }
    onDelete(user)
  }

  return (
    <div style={{ marginTop: 20 }}>
      {!isOnlyPrivate && (
        <div style={{ padding: 10 }}>
          <Typography
            sx={{ fontSize: 16, fontWeight: "600", flex: 1, textAlign: "center" }}
          >
            {T("select_post_type")}
          </Typography>
          <Separator color={colors.farm_green200} />
          <RadioGroup
            style={{ pointerEvents: isEdit ? "none" : "auto" }}
            aria-labelledby="radio-buttons-group-label"
            defaultValue="0"
            name="radio-buttons-group"
            value={isPrivate}
            onChange={(e) => handleThreadType(e.target.value)}
          >
            <FormControlLabel
              value={false}
              disabled={isEdit && isPrivate}
              control={
                <Radio
                  sx={{
                    "&, &.Mui-checked": {
                      color: colors.farm_blue200,
                    },
                  }}
                />
              }
              label={
                <div>
                  <Typography
                    sx={{ fontWeight: "600", mv: 12 }}
                    variant="body2"
                    color={isEdit && isPrivate ? "text.disabled" : "textPrimary"}
                  >
                    {T("post_type_open")}
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    {T("open_post_description")}
                  </Typography>
                </div>
              }
              labelPlacement="end"
              sx={{
                paddingTop: 2,
                alignItems: "flex-start",
              }}
            />
            <FormControlLabel
              value={true}
              disabled={isEdit && !isPrivate}
              control={
                <Radio
                  sx={{
                    "&, &.Mui-checked": {
                      color: colors.farm_blue200,
                    },
                  }}
                />
              }
              label={
                <div>
                  <Typography
                    sx={{ fontWeight: "600", mv: 12 }}
                    variant="body2"
                    color={isEdit && !isPrivate ? "text.disabled" : "textPrimary"}
                  >
                    {T("private_post")}
                  </Typography>

                  <Typography variant="body2" color="textSecondary">
                    {T("private_post_description")}
                  </Typography>
                </div>
              }
              labelPlacement="end"
              sx={{
                paddingTop: 6,
                alignItems: "flex-start",
              }}
            />
          </RadioGroup>
        </div>
      )}
      {isPrivate && (
        <div
          style={{
            marginBottom: 8,
            padding: 10,
            backgroundColor: colors.farm_gray10,
          }}
        >
          <Box display="flex" alignItems="center">
            <Typography variant="body2" color="textPrimary">
              {T("post_private_users")}
            </Typography>
            <Typography
              variant="body2"
              style={{
                backgroundColor: colors.farm_green500,
                borderRadius: "4px",
                color: "white",
                padding: "2px 8px",
                marginLeft: "8px",
                marginBottom: "2px",
              }}
            >
              {privateUsers.length}
            </Typography>
          </Box>

          <Separator color={colors.farm_green200} />
          {/* <div> */}
          <Carousel
            responsive={{
              superLargeDesktop: {
                breakpoint: { max: 4000, min: 1024 },
                items: 5, // Number of items to show on large screens
              },
              desktop: { breakpoint: { max: 1024, min: 768 }, items: 3 },
              tablet: { breakpoint: { max: 768, min: 464 }, items: 2 },

              mobile: {
                breakpoint: { max: 464, min: 0 },
                items: 2, // Ensure at least 2 items on mobile screens
              },
            }}
            swipeable={true} // Allows swipe gestures on mobile devices
            draggable={true} // Enables dragging on desktop
            showDots={false} // Optionally show/hide dots
            arrows={true} // Optionally show/hide arrows
            infinite={false} // Optionally make the carousel infinite
            keyBoardControl={true} // Allows keyboard control
            itemClass="carousel-item-padding" // Item class
            ref={carouselRef}
          >
            {privateUsers.slice().reverse().map((privateUser: PrivateUser) => (
              <SelectedUser
                isEdit={isEdit}
                key={privateUser.userId}
                privateUser={privateUser}
                onDelete={() => onDelete(privateUser)}
              />
            ))}
          </Carousel>

          <ButtonOutlinedCentered
            title={T("add_users")}
            onClick={handleAddUserClick}
          />

          {isUserModelVisible && (
            <ModalBackground onClick={handleCloseModal}>
              <ModalContainer onClick={(e: any) => e.stopPropagation()}>
                <AddUserModal
                  isEdit={false}
                  onAdd={onAdd}
                  onDelete={handleDelete}
                  closeModal={() => handleCloseModal()}
                />
              </ModalContainer>
            </ModalBackground>
          )}
        </div>
      )}
    </div>
  );
}
